import React, { useRef, useEffect, ReactNode } from "react";
import A11yDialog from "a11y-dialog";
import styled from "styled-components";
import ReactDOM from "react-dom";
import { scaleUp } from "./Styles";

export enum FullScreenDialogEvent {
  OPEN = "full-screen-dialog-open",
  CLOSE = "full-screen-dialog-open",
}

const Container = styled.div<{
  open: boolean;
  disableAnimation: boolean;
  inactive: boolean;
}>`
  visibility: ${({ open }) => (open ? "visible" : "hidden")};
  // when inactive (potential dialog overtop) stay visible to
  // fill in the background of the child dialog
  z-index: ${({ inactive }) => (inactive ? "auto" : "1000")};
  // Position fixed required for mobile. Absolute interacts poorly with mobile header/footer
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  ${({ open, disableAnimation }) => (!disableAnimation ? scaleUp(open) : "")};
`;

enum FullScreenDialogCloseReasonType {
  /* the dialog was unmounted (e.g. content flips between dialog/inline)
    aka "responsive modal */
  Unmount = "Unmount",
  /* the dialog was deactivated temporarily (e.g. another child dialog is running) */
  Deactivate = "Deactivate",
}

interface FullScreenDialogCloseEvent {
  // a11y-dialog forwards this via the close event
  // lack of event object means closed "normally"
  reason: FullScreenDialogCloseReasonType;
}

export interface FullScreenDialogProps {
  open: boolean;
  label: string;
  children: ReactNode;
  className?: string;
  onRequestClose: (event?: FullScreenDialogCloseEvent) => void;
  disableAnimation?: boolean;
  // temp close the dialog (in terms of focus trap) but keep the visuals
  // e.g. child dialog has launched
  inactive?: boolean;
}

export default function FullScreenDialog({
  open,
  label,
  children,
  className = "",
  disableAnimation = false,
  onRequestClose,
  inactive = false,
}: FullScreenDialogProps) {
  const dialogContainer = useRef<HTMLDivElement>(null);
  const a11yDialog = useRef<A11yDialog>();
  const root = useRef(document.getElementById("root"));

  useEffect(() => {
    // Make sure a11yDialog hasn't already been initialize. This code could run again if onRequestClose changes its reference
    if (!a11yDialog.current) {
      a11yDialog.current = new A11yDialog(
        dialogContainer.current,
        root.current
      );
      a11yDialog.current?.on("hide", (element, event) => {
        onRequestClose?.(event);
      });
    }
  }, [dialogContainer, onRequestClose]);

  useEffect(() => {
    return () => {
      if (open) {
        // Unmounting when open is an implicit hide otherwise a11y-dialog
        // keeps listening to focus events in the unmounted dialog.
        // e.g. when reverse-portaling dialog->inline via a media query
        a11yDialog.current?.hide({
          reason: FullScreenDialogCloseReasonType.Unmount,
        });
      }
    };
  }, [open]);

  useEffect(() => {
    if (open && !inactive) {
      // can't just rely on a11ydialog code as responsive modal media
      // breakpoint can happen at anytime
      // so need more nuance such as restoring back to content doc
      window.dispatchEvent(
        new CustomEvent(FullScreenDialogEvent.OPEN, {
          detail: { activeEl: document.activeElement },
        })
      );
      a11yDialog.current?.show();
    } else if (open && inactive) {
      // inactive is used in nested dialogs, this dialog pauses
      // in favor of a child dialog
      a11yDialog.current?.hide({
        reason: FullScreenDialogCloseReasonType.Deactivate,
      });
    }
  }, [open, a11yDialog, inactive]);

  return ReactDOM.createPortal(
    <Container
      open={open}
      disableAnimation={disableAnimation}
      inactive={inactive}
      aria-label={label}
      role="dialog"
      ref={dialogContainer}
      className={className}
    >
      {open && children}
    </Container>,
    document.body
  );
}
